// Echo test — docs/06 §3: record ~5 s from the mic, play it back through the
// selected output with a live level meter. Verifies permission, device
// selection and the audio route entirely client-side before the first real
// call; no server component. Platform recording/playback is injected so the
// state flow is unit-testable.

import { Store, type ReadableStore } from '../store.js';
import type { TimerHost } from '../types.js';
import { REAL_TIMERS } from '../types.js';
import type { MediaStreamLike } from '../rtc/peer.js';

export const ECHO_RECORD_MS = 5_000;
export const LEVEL_INTERVAL_MS = 100;

export type EchoTestPhase = 'idle' | 'recording' | 'playing' | 'done' | 'error';

export interface EchoTestState {
  phase: EchoTestPhase;
  /** Live input (recording) or output (playing) level, 0..1. */
  level: number;
  /** Milliseconds into the current phase. */
  elapsedMs: number;
  error: string | null;
}

/** A finished recording, opaque to the engine (a Blob in the browser). */
export type EchoRecording = unknown;

export interface EchoRecorderLike {
  /** Current input level 0..1 (AnalyserNode RMS in the browser). */
  readLevel(): number;
  stop(): Promise<EchoRecording>;
}

export interface EchoTestDeps {
  getUserMedia(constraints: { audio: Record<string, unknown> }): Promise<MediaStreamLike>;
  /** Start capturing the given stream (MediaRecorder + AnalyserNode). */
  startRecorder(stream: MediaStreamLike): Promise<EchoRecorderLike>;
  /** Play a finished recording through the selected output; resolves when done. */
  play(recording: EchoRecording, onLevel: (level: number) => void): Promise<void>;
  timers?: TimerHost;
}

const INITIAL: EchoTestState = { phase: 'idle', level: 0, elapsedMs: 0, error: null };

export class EchoTest {
  private readonly store = new Store<EchoTestState>(INITIAL);
  private readonly timers: TimerHost;
  private ticker: unknown = null;
  private cancelled = false;

  constructor(private readonly deps: EchoTestDeps) {
    this.timers = deps.timers ?? REAL_TIMERS;
  }

  get state(): ReadableStore<EchoTestState> {
    return this.store;
  }

  /** Run one full record-then-playback cycle. Safe to call repeatedly. */
  async run(): Promise<void> {
    if (this.store.get().phase === 'recording' || this.store.get().phase === 'playing') return;
    this.cancelled = false;
    let stream: MediaStreamLike;
    try {
      stream = await this.deps.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      this.store.set({ phase: 'error', level: 0, elapsedMs: 0, error: describe(err) });
      return;
    }

    try {
      const recorder = await this.deps.startRecorder(stream);
      const startedAt = this.timers.now();
      this.store.set({ phase: 'recording', level: 0, elapsedMs: 0, error: null });
      this.startTicker(() => {
        this.store.patch({
          level: clamp01(recorder.readLevel()),
          elapsedMs: this.timers.now() - startedAt,
        });
      });
      await this.wait(ECHO_RECORD_MS);
      this.stopTicker();
      const recording = await recorder.stop();
      if (this.cancelled) return;

      const playStartedAt = this.timers.now();
      this.store.set({ phase: 'playing', level: 0, elapsedMs: 0, error: null });
      this.startTicker(() => {
        this.store.patch({ elapsedMs: this.timers.now() - playStartedAt });
      });
      await this.deps.play(recording, (level) => this.store.patch({ level: clamp01(level) }));
      this.stopTicker();
      if (!this.cancelled) this.store.set({ phase: 'done', level: 0, elapsedMs: 0, error: null });
    } catch (err) {
      this.stopTicker();
      this.store.set({ phase: 'error', level: 0, elapsedMs: 0, error: describe(err) });
    } finally {
      for (const track of stream.getAudioTracks()) track.stop?.();
    }
  }

  cancel(): void {
    this.cancelled = true;
    this.stopTicker();
    this.store.set(INITIAL);
  }

  private startTicker(tick: () => void): void {
    this.stopTicker();
    this.ticker = this.timers.setInterval(tick, LEVEL_INTERVAL_MS);
  }

  private stopTicker(): void {
    if (this.ticker !== null) {
      this.timers.clearInterval(this.ticker);
      this.ticker = null;
    }
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => this.timers.setTimeout(resolve, ms));
  }
}

function clamp01(x: number): number {
  return Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0;
}

function describe(err: unknown): string {
  if (err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
    return 'Microphone access was denied — allow the microphone for Merkury in your system settings and try again.';
  }
  if (err instanceof Error && err.name === 'NotFoundError') {
    return 'No microphone found on this device.';
  }
  return err instanceof Error ? err.message : String(err);
}
